import Phaser from 'phaser';
import { GAME_HEIGHT, GAME_WIDTH } from '../data/config';
import { DINOS, DINO_ORDER, DinoId } from '../data/dinos';
import { CommsBox, COMMS } from '../objects/CommsBox';
import { Music } from '../objects/Music';

const PLANET_Y = 130;
const PLANET_SCALE = 1.0;

/**
 * Level Select — planet-map view. Four planets sit in a row across the
 * middle of the screen, each with its dino hovering above it. Click a
 * planet to PRACTICE that level, or hit the GAUNTLET button below to run
 * all four back-to-back.
 *
 * Keyboard nav: arrows drive everything. The focus state moves through
 * three rows — top (FULLSCREEN), middle (4 planets), bottom (GAUNTLET,
 * SCORES). Enter/Space activates whatever's focused. Tab cycles forward
 * through every focusable item as a fallback.
 */
type Focus = 'planet' | 'gauntlet' | 'scores' | 'fullscreen';

export class CharacterSelectScene extends Phaser.Scene {
  private cursor = 0;
  private dinoSprites: Phaser.GameObjects.Sprite[] = [];
  private planets: Phaser.GameObjects.Image[] = [];
  private highlightRing!: Phaser.GameObjects.Graphics;
  private nameText!: Phaser.GameObjects.Text;
  private planetText!: Phaser.GameObjects.Text;
  private statText!: Phaser.GameObjects.Text;
  private blurbText!: Phaser.GameObjects.Text;
  private gauntletBtn!: Phaser.GameObjects.Rectangle;
  private gauntletLabel!: Phaser.GameObjects.Text;
  private scoresBtn?: Phaser.GameObjects.Rectangle;
  private scoresLabel?: Phaser.GameObjects.Text;
  private fsBtn?: Phaser.GameObjects.Rectangle;
  private fsLabel?: Phaser.GameObjects.Text;
  private acceptingInput = true;
  private focus: Focus = 'planet';

  constructor() {
    super('CharacterSelect');
  }

  create() {
    this.acceptingInput = true;
    this.cursor = 0;
    this.focus = 'planet';
    this.dinoSprites = [];
    this.planets = [];
    this.scoresBtn = undefined;
    this.scoresLabel = undefined;
    this.fsBtn = undefined;
    this.fsLabel = undefined;

    // Same upbeat menu anthem from the title scene continues here. If we
    // arrived from a planet (Music.stop fires on Game shutdown) it kicks
    // back in fresh.
    Music.start('menu');

    // Deep-space starfield backdrop with subtle planet-color glints.
    this.cameras.main.setBackgroundColor(0x05070f);
    for (let i = 0; i < 90; i++) {
      const x = Phaser.Math.Between(0, GAME_WIDTH);
      const y = Phaser.Math.Between(0, GAME_HEIGHT);
      const planetColor = DINOS[DINO_ORDER[i % 4]].stripe;
      const star = this.add
        .image(x, y, 'px')
        .setAlpha(Phaser.Math.FloatBetween(0.3, 1))
        .setTint(Math.random() < 0.18 ? planetColor : 0xffffff);
      // Subtle parallax twinkle
      this.tweens.add({
        targets: star,
        alpha: { from: star.alpha, to: star.alpha * 0.4 },
        duration: Phaser.Math.Between(1200, 3000),
        yoyo: true,
        repeat: -1,
      });
    }

    // Big chunky title.
    this.add
      .text(GAME_WIDTH / 2, 24, 'ASTRODINOS', {
        fontFamily: '"Press Start 2P", "VT323", monospace',
        fontSize: '20px',
        color: '#ffe23a',
        fontStyle: 'bold',
        stroke: '#000000',
        strokeThickness: 3,
      })
      .setOrigin(0.5);
    this.add
      .text(GAME_WIDTH / 2, 46, 'pick a planet', {
        fontFamily: '"VT323", monospace',
        fontSize: '12px',
        color: '#ffffff',
      })
      .setOrigin(0.5)
      .setAlpha(0.7);

    // Highlight ring (under the planets, drawn as a Graphics so we can
    // animate radius/alpha for a cursor pulse).
    this.highlightRing = this.add.graphics().setDepth(10);

    // 4 planets across the middle, each with its dino floating above.
    const slot = GAME_WIDTH / (DINO_ORDER.length + 1);
    DINO_ORDER.forEach((id, i) => {
      const cx = (i + 1) * slot;

      // Planet
      const planet = this.add
        .image(cx, PLANET_Y, `planet-${id}`)
        .setScale(PLANET_SCALE);
      planet.setInteractive({ useHandCursor: true });
      planet.on('pointerdown', () => this.tap(i));
      planet.on('pointerover', () => this.hover(i));
      // Slow planet bob
      this.tweens.add({
        targets: planet,
        y: PLANET_Y + 2,
        duration: 1800 + i * 240,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      this.planets.push(planet);

      // Dino above the planet (idle anim)
      const dino = this.add
        .sprite(cx, PLANET_Y - 50, `dino-${id}`)
        .setScale(2.5)
        .play(`${id}-idle`);
      dino.setInteractive({ useHandCursor: true });
      dino.on('pointerdown', () => this.tap(i));
      dino.on('pointerover', () => this.hover(i));
      this.tweens.add({
        targets: dino,
        y: PLANET_Y - 56,
        duration: 900 + i * 100,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
      this.dinoSprites.push(dino);
    });
    // No per-planet labels — the focused stat panel below is the single
    // source of truth, otherwise GLACIER/INFERNO/DUNES/CANOPY collide with
    // the DOUX/MORT/etc heading visually.

    // Stat panel below planets — name, planet, stats, blurb. Pushed down
    // a few px so the bold dino name (e.g. "BISCUIT") doesn't kiss the
    // bottom of the highlighted planet (which scales up to 1.15).
    this.nameText = this.add
      .text(GAME_WIDTH / 2, 184, '', {
        fontFamily: '"Press Start 2P", "VT323", monospace',
        fontSize: '14px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.planetText = this.add
      .text(GAME_WIDTH / 2, 200, '', {
        fontFamily: '"VT323", monospace',
        fontSize: '11px',
        color: '#ffe23a',
      })
      .setOrigin(0.5);
    this.statText = this.add
      .text(GAME_WIDTH / 2, 213, '', {
        fontFamily: '"VT323", monospace',
        fontSize: '11px',
        color: '#ffffff',
      })
      .setOrigin(0.5);
    this.blurbText = this.add
      .text(GAME_WIDTH / 2, 226, '', {
        fontFamily: '"VT323", monospace',
        fontSize: '11px',
        color: '#ffffff',
        align: 'center',
        wordWrap: { width: GAME_WIDTH - 40 },
      })
      .setOrigin(0.5)
      .setAlpha(0.85);

    // GAUNTLET button. Stars flanking the label use the actual star asset
    // rather than the ★ glyph — Press Start 2P has no star glyph so it
    // falls back to a different font and renders them at a tiny mismatched
    // size next to the bold pixel text. Button height bumped 22→28 so the
    // 10px label has comfortable padding top + bottom.
    const gy = GAME_HEIGHT - 22;
    this.gauntletBtn = this.add
      .rectangle(GAME_WIDTH / 2, gy, 200, 28, 0x0d0a1a, 0.9)
      .setStrokeStyle(2, 0xffe23a)
      .setInteractive({ useHandCursor: true });
    this.gauntletLabel = this.add
      .text(GAME_WIDTH / 2, gy, 'RUN THE GAUNTLET', {
        fontFamily: '"Press Start 2P", "VT323", monospace',
        fontSize: '10px',
        color: '#ffe23a',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    // Real star graphics flanking the label, color-matched + scaled to
    // sit nicely next to the 10px pixel text.
    const labelHalfW = this.gauntletLabel.width / 2;
    this.add.image(GAME_WIDTH / 2 - labelHalfW - 10, gy, 'star').setTint(0xffe23a).setScale(1.1);
    this.add.image(GAME_WIDTH / 2 + labelHalfW + 10, gy, 'star').setTint(0xffe23a).setScale(1.1);
    this.gauntletBtn.on('pointerover', () => {
      this.focus = 'gauntlet';
      this.refresh();
    });
    this.gauntletBtn.on('pointerdown', () => this.startGauntlet());

    // Small LEADERBOARD button — opens the standalone scores browser.
    // Sits to the right of the gauntlet button, lower priority styling
    // (smaller, no glow) since it's secondary navigation.
    this.scoresBtn = this.add
      .rectangle(GAME_WIDTH - 50, gy, 80, 18, 0x1a1530, 0.9)
      .setStrokeStyle(1, 0x9ad8ff)
      .setInteractive({ useHandCursor: true });
    this.scoresLabel = this.add
      .text(GAME_WIDTH - 50, gy, 'SCORES', {
        fontFamily: '"VT323", monospace',
        fontSize: '10px',
        color: '#9ad8ff',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.scoresBtn.on('pointerover', () => {
      this.focus = 'scores';
      this.refresh();
    });
    this.scoresBtn.on('pointerdown', () => this.openScores());

    // Fullscreen toggle — bigger / more obvious now so accidental Ctrl+W
    // doesn't keep killing runs. Sits top-right with a label "FULLSCREEN".
    // When active, requests Keyboard Lock so OS-reserved hotkeys (Tab,
    // F-keys, Esc, Cmd+W, Ctrl+T) get captured by the game instead of
    // the browser. Click again or press Esc to exit.
    if (typeof document.documentElement.requestFullscreen === 'function') {
      const fsW = 90;
      const fsH = 18;
      const fsX = GAME_WIDTH - fsW / 2 - 4;
      const fsY = 14;
      this.fsBtn = this.add
        .rectangle(fsX, fsY, fsW, fsH, 0x1a1530, 0.9)
        .setStrokeStyle(2, 0xffe23a)
        .setInteractive({ useHandCursor: true });
      this.fsLabel = this.add
        .text(fsX, fsY, '⛶  FULLSCREEN', {
          fontFamily: '"VT323", monospace',
          fontSize: '11px',
          color: '#ffe23a',
          fontStyle: 'bold',
        })
        .setOrigin(0.5);
      // Subtle pulse so it actually catches the eye on first visit.
      this.tweens.add({
        targets: this.fsBtn,
        alpha: { from: 1, to: 0.7 },
        duration: 1000,
        yoyo: true,
        repeat: -1,
      });
      this.fsBtn.on('pointerover', () => {
        this.focus = 'fullscreen';
        this.refresh();
      });
      this.fsBtn.on('pointerdown', () => this.toggleFullscreen());
    }

    // Inputs. Arrows drive the entire menu — Up/Down moves between rows
    // (FULLSCREEN ↔ planets ↔ GAUNTLET/SCORES), Left/Right moves within
    // the active row. Enter/Space activates whatever is focused. Tab is
    // kept as a forward-cycle fallback so power users still have a
    // single-key way to walk the menu.
    const keys = this.input.keyboard!.addKeys({
      up: Phaser.Input.Keyboard.KeyCodes.UP,
      down: Phaser.Input.Keyboard.KeyCodes.DOWN,
      left: Phaser.Input.Keyboard.KeyCodes.LEFT,
      right: Phaser.Input.Keyboard.KeyCodes.RIGHT,
      w: Phaser.Input.Keyboard.KeyCodes.W,
      s: Phaser.Input.Keyboard.KeyCodes.S,
      a: Phaser.Input.Keyboard.KeyCodes.A,
      d: Phaser.Input.Keyboard.KeyCodes.D,
      enter: Phaser.Input.Keyboard.KeyCodes.ENTER,
      space: Phaser.Input.Keyboard.KeyCodes.SPACE,
      tab: Phaser.Input.Keyboard.KeyCodes.TAB,
      g: Phaser.Input.Keyboard.KeyCodes.G,
    }) as Record<string, Phaser.Input.Keyboard.Key>;

    const moveLeft = () => {
      if (this.focus === 'planet') {
        this.cursor = (this.cursor - 1 + DINO_ORDER.length) % DINO_ORDER.length;
      } else if (this.focus === 'scores') {
        this.focus = 'gauntlet';
      }
      this.refresh();
    };
    const moveRight = () => {
      if (this.focus === 'planet') {
        this.cursor = (this.cursor + 1) % DINO_ORDER.length;
      } else if (this.focus === 'gauntlet') {
        if (this.scoresBtn) this.focus = 'scores';
      }
      this.refresh();
    };
    const moveUp = () => {
      if (this.focus === 'planet') {
        this.focus = this.fsBtn ? 'fullscreen' : 'scores';
      } else if (this.focus === 'gauntlet' || this.focus === 'scores') {
        this.focus = 'planet';
      } else if (this.focus === 'fullscreen') {
        this.focus = 'gauntlet';
      }
      this.refresh();
    };
    const moveDown = () => {
      if (this.focus === 'fullscreen') {
        this.focus = 'planet';
      } else if (this.focus === 'planet') {
        this.focus = 'gauntlet';
      } else if (this.focus === 'gauntlet' || this.focus === 'scores') {
        this.focus = this.fsBtn ? 'fullscreen' : 'planet';
      }
      this.refresh();
    };
    // Tab cycles forward through every focusable item: fullscreen →
    // planet0..3 → gauntlet → scores → wrap.
    const tabForward = () => {
      const order: Focus[] = [];
      if (this.fsBtn) order.push('fullscreen');
      for (let i = 0; i < DINO_ORDER.length; i++) order.push('planet');
      order.push('gauntlet');
      if (this.scoresBtn) order.push('scores');

      // Find current index. For 'planet', the index in the cycle depends
      // on this.cursor.
      let idx: number;
      if (this.focus === 'planet') {
        idx = (this.fsBtn ? 1 : 0) + this.cursor;
      } else {
        idx = order.indexOf(this.focus);
      }
      const next = (idx + 1) % order.length;
      const target = order[next];
      if (target === 'planet') {
        const planetStart = this.fsBtn ? 1 : 0;
        this.focus = 'planet';
        this.cursor = next - planetStart;
      } else {
        this.focus = target;
      }
      this.refresh();
    };

    keys.left.on('down', moveLeft);
    keys.a.on('down', moveLeft);
    keys.right.on('down', moveRight);
    keys.d.on('down', moveRight);
    keys.up.on('down', moveUp);
    keys.w.on('down', moveUp);
    keys.down.on('down', moveDown);
    keys.s.on('down', moveDown);
    keys.tab.on('down', tabForward);
    keys.g.on('down', () => this.startGauntlet());

    const confirm = () => {
      switch (this.focus) {
        case 'gauntlet':
          this.startGauntlet();
          break;
        case 'scores':
          this.openScores();
          break;
        case 'fullscreen':
          this.toggleFullscreen();
          break;
        case 'planet':
        default:
          this.startPractice(DINO_ORDER[this.cursor]);
          break;
      }
    };
    keys.enter.on('down', confirm);
    keys.space.on('down', confirm);

    // Dev shortcuts — gated on god mode so they work on localhost AND on
    // production via `?god` URL flag. Useful right now for end-to-end
    // testing of the Teams webhook + leaderboard pipeline directly on
    // astrodinos.com. TIGHTEN THIS to `import.meta.env.DEV` once the
    // production pipeline is verified — that tree-shakes the block out
    // of the prod bundle entirely.
    //   F        → fake high score (8500, 1:15) — fast time so we can also
    //              submit a second high score with a slower time and verify
    //              the time-tiebreak ordering on the leaderboard.
    //   Shift+F  → fake low score (250, 8:45) — likely won't crack top 10,
    //              lets us verify the "you didn't make top 10" UX
    const w = window as unknown as { __god?: boolean };
    if (w.__god) {
      this.input.keyboard?.on('keydown-F', (e: KeyboardEvent) => {
        const highScore = !e.shiftKey;
        const fakeScore = highScore ? 8500 : 250;
        const fakeTimeMs = highScore ? 75_000 : 525_000;
        this.scene.start('PlanetCleared', {
          score: fakeScore,
          dinoId: 'vita',
          runStartMs: Date.now() - fakeTimeMs,
        });
      });
    }

    this.refresh();

    // Mr Consequence cuts in to dare the player into the gauntlet — sets
    // the rivalry tone before they've even picked a planet. Delayed ~1.4s
    // so the planet-select layout has settled and the player isn't reading
    // a comms bubble at the same time as the planet stat panel changing.
    const comms = new CommsBox(this);
    this.time.delayedCall(1400, () => comms.say(COMMS.villainLevelSelect()));
  }

  private hover(i: number) {
    if (this.cursor === i && this.focus === 'planet') return;
    this.cursor = i;
    this.focus = 'planet';
    this.refresh();
  }

  /** First click selects, second click on the same planet starts. */
  private tap(i: number) {
    if (!this.acceptingInput) return;
    if (this.cursor === i && this.focus === 'planet') {
      this.startPractice(DINO_ORDER[i]);
    } else {
      this.cursor = i;
      this.focus = 'planet';
      this.refresh();
    }
  }

  private refresh() {
    const id = DINO_ORDER[this.cursor];
    const stats = DINOS[id];
    const planetFocused = this.focus === 'planet';

    // Move highlight ring under the focused planet (or hide it when focus
    // has moved off the planet row to gauntlet/scores/fullscreen).
    this.highlightRing.clear();
    if (planetFocused) {
      const cx = this.planets[this.cursor].x;
      const cy = PLANET_Y;
      this.highlightRing.lineStyle(2, stats.stripe, 0.9);
      this.highlightRing.strokeCircle(cx, cy, 36);
      this.highlightRing.lineStyle(1, 0xffffff, 0.6);
      this.highlightRing.strokeCircle(cx, cy, 40);
    }

    // Scale focused planet/dino up a touch
    this.planets.forEach((p, i) => {
      this.tweens.add({
        targets: p,
        scale: i === this.cursor && planetFocused ? 1.15 : 1,
        duration: 160,
      });
    });
    this.dinoSprites.forEach((d, i) => {
      this.tweens.add({
        targets: d,
        scale: i === this.cursor && planetFocused ? 3 : 2.5,
        duration: 160,
      });
    });

    const gauntletFocused = this.focus === 'gauntlet';
    this.gauntletBtn.setStrokeStyle(gauntletFocused ? 3 : 2, 0xffe23a);
    this.gauntletBtn.setScale(gauntletFocused ? 1.06 : 1);
    this.gauntletLabel.setScale(gauntletFocused ? 1.06 : 1);

    if (this.scoresBtn && this.scoresLabel) {
      const scoresFocused = this.focus === 'scores';
      this.scoresBtn.setStrokeStyle(scoresFocused ? 3 : 1, 0x9ad8ff);
      this.scoresBtn.setScale(scoresFocused ? 1.1 : 1);
      this.scoresLabel.setScale(scoresFocused ? 1.1 : 1);
    }
    if (this.fsBtn && this.fsLabel) {
      const fsFocused = this.focus === 'fullscreen';
      this.fsBtn.setStrokeStyle(fsFocused ? 3 : 2, 0xffe23a);
      this.fsBtn.setScale(fsFocused ? 1.08 : 1);
      this.fsLabel.setScale(fsFocused ? 1.08 : 1);
    }

    this.nameText.setText(stats.name);
    this.nameText.setColor(`#${stats.stripe.toString(16).padStart(6, '0')}`);
    this.planetText.setText(`PLANET: ${stats.planet}`);
    this.statText.setText(`HP ${stats.maxHp}   SPEED ${stats.speed}   GUN ${stats.kickDamage}`);
    this.blurbText.setText(stats.blurb);
  }

  private openScores() {
    if (!this.acceptingInput) return;
    this.scene.start('Leaderboard');
  }

  private async toggleFullscreen() {
    if (!this.fsLabel) return;
    if (document.fullscreenElement) {
      await document.exitFullscreen().catch(() => undefined);
      this.fsLabel.setText('⛶  FULLSCREEN');
    } else {
      try {
        await document.documentElement.requestFullscreen();
        // Lock keyboard if the browser supports it. This is what
        // captures Tab, F-keys, Esc, Cmd+W, etc. that otherwise go
        // to the browser. Chrome/Edge support it; Firefox/Safari
        // don't yet.
        const nav = navigator as Navigator & {
          keyboard?: { lock?: () => Promise<void>; unlock?: () => void };
        };
        await nav.keyboard?.lock?.().catch(() => undefined);
        this.fsLabel.setText('⛶  EXIT FULLSCREEN');
      } catch {
        // User denied or browser blocked — silently no-op.
      }
    }
  }

  private startPractice(id: DinoId) {
    if (!this.acceptingInput) return;
    this.acceptingInput = false;
    this.cameras.main.flash(180, 255, 255, 255);
    this.time.delayedCall(220, () => {
      this.scene.start('Game', { dinoId: id, mode: 'practice', runScore: 0 });
    });
  }

  private startGauntlet() {
    if (!this.acceptingInput) return;
    this.acceptingInput = false;
    this.cameras.main.flash(220, 255, 226, 58);
    this.time.delayedCall(260, () => {
      this.scene.start('Game', { dinoId: 'doux', mode: 'gauntlet', runScore: 0 });
    });
  }
}
